<template>
  <div ref="driveFilterRef" class="drive-filter">
    <a
      v-for="drive in driverMap"
      :key="drive.attrType"
      class="drive-chip"
      :class="{ active: drive.attrType === selectedAttrType }"
      :href="pageHref(1, drive.attrType, keyword)"
      :ref="(el) => setDriveRef(el, drive.attrType)"
    >
      {{ drive.driverName }}
    </a>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from "vue";

type DriverInfo = {
  attrType: number;
  driverName: string;
};

const props = defineProps<{
  driverMap: DriverInfo[];
  selectedAttrType: number;
  keyword?: string;
  pageHref: (page: number, attrType: number, likeTitle?: string) => string;
}>();

const driveFilterRef = ref<HTMLElement | null>(null);
const driveRefs = ref(new Map<number, HTMLElement>());

const setDriveRef = (el: Element | null, key: number) => {
  if (!el) {
    driveRefs.value.delete(key);
    return;
  }
  driveRefs.value.set(key, el as HTMLElement);
};

const scrollDriveIntoView = () => {
  const container = driveFilterRef.value;
  if (!container) return;
  const activeEl = driveRefs.value.get(props.selectedAttrType);
  if (!activeEl) return;
  const target = activeEl.offsetLeft - (container.clientWidth - activeEl.clientWidth) / 2;
  container.scrollTo({ left: Math.max(0, target) });
};

watch(
  () => props.selectedAttrType,
  () => {
    nextTick(scrollDriveIntoView);
  },
  { immediate: true }
);

onMounted(() => {
  nextTick(scrollDriveIntoView);
});
</script>

<style scoped>
.drive-filter {
  display: flex;
  gap: 8px;
  padding: 10px 4px 4px;
  overflow-x: auto;
  scrollbar-width: none;
}

.drive-filter::-webkit-scrollbar {
  display: none;
}

.drive-chip {
  border: 1px solid #f3c7ba;
  background: #fff;
  color: #7a5d57;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  text-decoration: none;
}

.drive-chip.active {
  background: var(--accent);
  color: #fff;
  border-color: var(--accent);
  box-shadow: 0 8px 18px rgba(242, 105, 75, 0.24);
}
</style>
